import { FaPencilAlt, FaTrashAlt, FaEye } from "react-icons/fa";
import { ImExit } from "react-icons/im";
import { Assistant } from "../../../api/models";
import { ButtonTooltip } from "../../ButtonTooltip";

type SharedAssistant = Assistant & { isJtc: boolean };

interface OwnedTableProps {
    variant: "owned";
    data: Assistant[];
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}

interface SharedTableProps {
    variant: "shared";
    data: SharedAssistant[];
    userGroups: string[];
    onView: (id: string) => void;
    onLeave: (id: string) => void;
}

type AssistantTableProps = OwnedTableProps | SharedTableProps;

export const AssistantTable = (props: AssistantTableProps) => {
    return (
        <table className="table-fixed min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <th style={{ width: '35%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Title
                    </th>
                    <th style={{ width: '25%' }} className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date Created
                    </th>
                    <th style={{ width: '25%' }} className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Creator
                    </th>
                    <th style={{ width: '15%' }} className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                    </th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {props.variant === "owned"
                    ? props.data.map((assist) => (
                        <tr key={assist.id} className="hover:bg-gray-100">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {assist.title}
                            </td>
                            <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(assist.created_at).toLocaleDateString()}
                            </td>
                            <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {assist.user_display_name || "JTCGroup"}
                            </td>
                            <td className="px-2 py-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end gap-3 pr-2">
                                    <ButtonTooltip
                                        title="Assistant is learning... check back later"
                                        placement="top"
                                        disableHoverListener={assist.available}
                                    >
                                        <span className="inline-flex items-center">
                                            <button
                                                disabled={!assist.available}
                                                onClick={assist.available ? () => props.onEdit(assist.id) : undefined}
                                                className={
                                                    assist.available
                                                        ? "text-jtc-blue hover:text-blue-500"
                                                        : "text-gray-400 cursor-not-allowed"
                                                }
                                            >
                                                <FaPencilAlt className="w-4 h-4" />
                                            </button>
                                        </span>
                                    </ButtonTooltip>

                                    <span className="inline-flex items-center">
                                        <button
                                            className="text-jtc-pink hover:text-jtc-purple"
                                            onClick={() => props.onDelete(assist.id)}
                                        >
                                            <FaTrashAlt className="w-4 h-4" />
                                        </button>
                                    </span>
                                </div>
                            </td>
                        </tr>
                    ))
                    : props.data.map((assist) => (
                        <tr key={assist.id} className="hover:bg-gray-100">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {assist.title}
                            </td>
                            <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(assist.created_at).toLocaleDateString()}
                            </td>
                            {/* Creator column: Show "JTCGroup" if isJtc, else user_display_name or "Unknown" */}
                            <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {assist.isJtc ? "JTC Group" : assist.user_display_name || "Unknown"}
                            </td>
                            <td className="px-2 py-4 whitespace-nowrap text-right">
                                {/* If isJtc = true, show NO actions at all. */}
                                {assist.isJtc ? (
                                    <></>
                                ) : (
                                    <div className="flex justify-end gap-3 pr-2">
                                        <button
                                            className="text-jtc-blue hover:text-blue-500"
                                            onClick={() => props.onView(assist.id)}
                                        >
                                            <FaEye className="w-4 h-4" />
                                        </button>
                                        {/* Leave only makes sense for direct shares: group-granted
                                            access (incl. 'All Users') survives leaving. */}
                                        {(!assist.groups?.some(g => g === 'All Users' || props.userGroups.includes(g))) && (
                                            <button
                                                className="text-jtc-pink hover:text-jtc-purple"
                                                onClick={() => props.onLeave(assist.id)}
                                            >
                                                <ImExit className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
            </tbody>
        </table>
    );
};
